import { FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminDownloadRevisionUrl,
  adminGetPaperReviewDecision,
  adminSetPaperDecision,
  type ConferencePaperReviewDecision,
} from "../api";
import { formatDateTime, paperStatusLabel } from "../format";
import { PageHeader } from "../shells/PageHeader";

const DECISIONS = ["accept", "minor_revision", "major_revision", "reject"] as const;

export function AdminReviewDecisionPage() {
  const { paperId } = useParams<{ paperId: string }>();
  const { t, locale } = useLanguage();
  const [data, setData] = useState<ConferencePaperReviewDecision | null>(null);
  const [decision, setDecision] = useState("");
  const [message, setMessage] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const reload = async () => {
    if (!paperId) return;
    const d = await adminGetPaperReviewDecision(paperId);
    setData(d);
    if (d.paper.decision) setDecision(d.paper.decision);
    if (d.paper.decision_message) setMessage(d.paper.decision_message);
  };

  useEffect(() => {
    if (!paperId) return;
    setBusy(true);
    reload()
      .catch((e) => setError(e instanceof Error ? e.message : t.common.error))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paperId, t.common.error]);

  const recLabel = (value?: string | null) => {
    switch (value) {
      case "accept":
        return t.reviews.recAccept;
      case "minor_revision":
        return t.reviews.recMinor;
      case "major_revision":
        return t.reviews.recMajor;
      case "reject":
        return t.reviews.recReject;
      default:
        return value || "—";
    }
  };

  const decisionLabel = (value: string) => {
    switch (value) {
      case "accept":
        return t.admin.decisionAccept;
      case "minor_revision":
        return t.admin.decisionMinor;
      case "major_revision":
        return t.admin.decisionMajor;
      case "reject":
        return t.admin.decisionReject;
      default:
        return value;
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!paperId || !decision) {
      setError(t.admin.decisionNeedChoice);
      return;
    }
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      await adminSetPaperDecision(paperId, {
        decision,
        decision_message: message.trim() || null,
        force_update: forceUpdate,
      });
      setOkMsg(t.admin.decisionSaved);
      setForceUpdate(false);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  if (!data && !error) return <p>{t.common.loading}</p>;
  if (!data) {
    return (
      <div>
        <PageHeader
          title={t.admin.reviewDecisionTitle}
          backTo="/admin/papers"
          backLabel={t.admin.papers}
        />
        <div className="dn-alert dn-alert-error">{error || t.common.error}</div>
      </div>
    );
  }

  const paper = data.paper;
  const hasDecision = Boolean(paper.decided_at || paper.decision);

  return (
    <div>
      <PageHeader
        title={t.admin.reviewDecisionTitle}
        lead={t.admin.reviewDecisionLead}
        backTo="/admin/papers"
        backLabel={t.admin.papers}
      />

      <div className="dn-card" style={{ marginBottom: "1.25rem" }}>
        <h3>{paper.title}</h3>
        <p>
          <strong>{t.admin.colAuthor}:</strong> {paper.authors}
          <br />
          <strong>{t.admin.colLanguage}:</strong> {paper.language.toUpperCase()}
          <br />
          <strong>{t.admin.colStatus}:</strong>{" "}
          {paperStatusLabel(paper.status, paper.is_public, t)}
          {paper.keywords ? (
            <>
              <br />
              <strong>{t.paper.keywords}:</strong> {paper.keywords}
            </>
          ) : null}
        </p>
        {paper.abstract && (
          <p>
            <strong>{t.paper.abstract}:</strong> {paper.abstract}
          </p>
        )}
        {hasDecision && (
          <p>
            <strong>{t.admin.currentDecision}:</strong> {decisionLabel(paper.decision || "")}
            <br />
            <strong>{t.admin.decisionMessage}:</strong> {paper.decision_message || "—"}
            <br />
            <span className="dn-muted">
              {t.admin.decidedAt}:{" "}
              {paper.decided_at ? formatDateTime(paper.decided_at, locale) : "—"}
            </span>
            {paper.current_revision_number ? (
              <>
                <br />
                <strong>{t.admin.currentRevision}:</strong> {paper.current_revision_number}
              </>
            ) : null}
          </p>
        )}
      </div>

      {(data.revisions?.length || 0) > 0 && (
        <>
          <h2>{t.admin.manuscriptRevisions}</h2>
          <p className="dn-muted" style={{ marginBottom: "0.75rem" }}>
            {t.admin.manuscriptRevisionsHelp}
          </p>
          <ul style={{ marginBottom: "1.5rem" }}>
            {(data.revisions || []).map((r) => (
              <li key={r.id} style={{ marginBottom: 8 }}>
                <strong>
                  {t.admin.revisionNumber.replace("{n}", String(r.revision_number))}
                </strong>
                {r.is_latest ? ` (${t.admin.revisionLatest})` : ""}
                {" · "}
                {r.original_filename || "—"}
                {" · "}
                {formatDateTime(r.submitted_at, locale)}
                {r.revision_notes ? (
                  <div className="dn-muted" style={{ marginTop: 2 }}>
                    {r.revision_notes}
                  </div>
                ) : null}
                <div style={{ marginTop: 4 }}>
                  <button
                    type="button"
                    className="dn-btn dn-btn-ghost"
                    onClick={async () => {
                      try {
                        const d = await adminDownloadRevisionUrl(paper.id, r.id);
                        window.open(d.download_url, "_blank", "noopener,noreferrer");
                      } catch (err) {
                        setError(err instanceof Error ? err.message : t.common.error);
                      }
                    }}
                  >
                    {t.admin.downloadRevision}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <h2>{t.admin.reviewerFeedback}</h2>
      <p className="dn-muted" style={{ marginBottom: "0.75rem" }}>
        {t.admin.pendingReviewsCount
          .replace("{pending}", String(data.pending_reviews))
          .replace("{completed}", String(data.completed_reviews))}
      </p>
      <div className="dn-table-wrap" style={{ marginBottom: "1.5rem" }}>
        <table className="dn-table dn-table-responsive">
          <thead>
            <tr>
              <th>{t.admin.reviewerName}</th>
              <th>{t.admin.assignColStatus}</th>
              <th>{t.reviews.recommendation}</th>
              <th>{t.reviews.comments}</th>
              <th>{t.reviews.submittedAt}</th>
            </tr>
          </thead>
          <tbody>
            {data.reviewers.length === 0 ? (
              <tr>
                <td colSpan={5} className="dn-muted">
                  {t.admin.noReviewersAssigned}
                </td>
              </tr>
            ) : (
              data.reviewers.map((r) => (
                <tr key={r.assignment_id}>
                  <td data-label={t.admin.reviewerName}>
                    {r.reviewer_name}
                    <br />
                    <span className="dn-muted">{r.reviewer_institution}</span>
                  </td>
                  <td data-label={t.admin.assignColStatus}>
                    {r.is_pending ? (
                      <strong>{t.admin.reviewPending}</strong>
                    ) : (
                      t.reviews.statusCompleted
                    )}
                  </td>
                  <td data-label={t.reviews.recommendation}>
                    {r.is_pending ? "—" : recLabel(r.recommendation)}
                  </td>
                  <td data-label={t.reviews.comments}>{r.is_pending ? "—" : r.comments || "—"}</td>
                  <td data-label={t.reviews.submittedAt}>
                    {r.submitted_at ? formatDateTime(r.submitted_at, locale) : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {okMsg && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {okMsg}
        </div>
      )}

      <form className="dn-card" onSubmit={onSubmit}>
        <h3>{t.admin.recordDecision}</h3>
        <p className="dn-muted">{t.admin.decisionAdvisory}</p>
        <label style={{ display: "block", marginBottom: "0.75rem" }}>
          {t.admin.decisionLabel}
          <select
            value={decision}
            onChange={(e) => setDecision(e.target.value)}
            disabled={busy}
            style={{ display: "block", marginTop: 6, minWidth: 260 }}
            required
          >
            <option value="">{t.admin.decisionPlaceholder}</option>
            {DECISIONS.map((d) => (
              <option key={d} value={d}>
                {decisionLabel(d)}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: "block", marginBottom: "0.75rem" }}>
          {t.admin.decisionMessage}
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            disabled={busy}
            placeholder={t.admin.decisionMessageHelp}
            style={{ display: "block", width: "100%", marginTop: 6 }}
          />
        </label>
        {hasDecision && (
          <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: "0.75rem" }}>
            <input
              type="checkbox"
              checked={forceUpdate}
              onChange={(e) => setForceUpdate(e.target.checked)}
              disabled={busy}
            />
            {t.admin.forceUpdateDecision}
          </label>
        )}
        <button type="submit" className="dn-btn dn-btn-primary" disabled={busy}>
          {hasDecision ? t.admin.updateDecision : t.admin.saveDecision}
        </button>
      </form>

      {data.decision_history.length > 0 && (
        <>
          <h2 style={{ marginTop: "1.5rem" }}>{t.admin.decisionHistory}</h2>
          <ul>
            {data.decision_history.map((h) => (
              <li key={h.id}>
                {decisionLabel(h.decision)} · {formatDateTime(h.created_at, locale)}
                {h.is_update ? ` (${t.admin.decisionUpdate})` : ""}
                {h.decision_message ? ` — ${h.decision_message}` : ""}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
